import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Check, X } from 'lucide-react';

interface ScreenCaptureOverlayProps {
  screenshotUrl: string;
  onCapture: (file: File) => void;
  onCancel: () => void;
}

export const ScreenCaptureOverlay: React.FC<ScreenCaptureOverlayProps> = ({
  screenshotUrl,
  onCapture,
  onCancel,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [currentPoint, setCurrentPoint] = useState<{ x: number; y: number } | null>(null);
  const [selection, setSelection] = useState<{ x: number; y: number; w: number; h: number } | null>(null);

  // ESC 키로 취소
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const getRelativePosition = useCallback((e: React.MouseEvent) => {
    const container = containerRef.current;
    if (!container) return { x: 0, y: 0 };
    const rect = container.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // 좌클릭만
    const pos = getRelativePosition(e);
    setStartPoint(pos);
    setCurrentPoint(pos);
    setIsDrawing(true);
    setSelection(null);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing || !startPoint) return;
    setCurrentPoint(getRelativePosition(e));
  };

  const handleMouseUp = () => {
    if (!isDrawing || !startPoint || !currentPoint) return;
    setIsDrawing(false);

    const x = Math.min(startPoint.x, currentPoint.x);
    const y = Math.min(startPoint.y, currentPoint.y);
    const w = Math.abs(currentPoint.x - startPoint.x);
    const h = Math.abs(currentPoint.y - startPoint.y);

    // 너무 작은 영역은 무시
    if (w < 10 || h < 10) {
      setStartPoint(null);
      setCurrentPoint(null);
      return;
    }

    setSelection({ x, y, w, h });
  };

  const handleConfirm = () => {
    if (!selection || !imgRef.current) return;

    const img = imgRef.current;
    const container = containerRef.current;
    if (!container) return;

    // 화면에 표시된 이미지 크기 vs 원본 이미지 크기 비율 계산
    const scaleX = img.naturalWidth / container.clientWidth;
    const scaleY = img.naturalHeight / container.clientHeight;

    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = Math.round(selection.w * scaleX);
    cropCanvas.height = Math.round(selection.h * scaleY);
    const ctx = cropCanvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(
      img,
      Math.round(selection.x * scaleX),
      Math.round(selection.y * scaleY),
      Math.round(selection.w * scaleX),
      Math.round(selection.h * scaleY),
      0,
      0,
      cropCanvas.width,
      cropCanvas.height
    );

    cropCanvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
        onCapture(file);
      }
    }, 'image/png');
  };

  // 선택 영역 사각형 계산
  const getSelectionRect = () => {
    if (selection) return selection;
    if (!isDrawing || !startPoint || !currentPoint) return null;

    return {
      x: Math.min(startPoint.x, currentPoint.x),
      y: Math.min(startPoint.y, currentPoint.y),
      w: Math.abs(currentPoint.x - startPoint.x),
      h: Math.abs(currentPoint.y - startPoint.y),
    };
  };

  const rect = getSelectionRect();

  return (
    <div className="fixed inset-0 z-[9999]" style={{ cursor: isDrawing ? 'crosshair' : selection ? 'default' : 'crosshair' }}>
      {/* 숨겨진 원본 이미지 (크롭 계산용) */}
      <img
        ref={imgRef}
        src={screenshotUrl}
        alt=""
        className="hidden"
      />

      {/* 스크린샷 배경 */}
      <div
        ref={containerRef}
        className="absolute inset-0"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <img
          src={screenshotUrl}
          alt="스크린샷"
          className="w-full h-full object-fill"
          draggable={false}
        />

        {/* 어두운 오버레이 (선택 영역 외부) */}
        <div className="absolute inset-0 bg-black/50 pointer-events-none" />

        {/* 선택 영역 (밝게 표시) */}
        {rect && rect.w > 0 && rect.h > 0 && (
          <>
            {/* 선택 영역 클리어 윈도우 */}
            <div
              className="absolute border-2 border-white pointer-events-none"
              style={{
                left: rect.x,
                top: rect.y,
                width: rect.w,
                height: rect.h,
                backgroundImage: `url(${screenshotUrl})`,
                backgroundSize: `${containerRef.current?.clientWidth}px ${containerRef.current?.clientHeight}px`,
                backgroundPosition: `-${rect.x}px -${rect.y}px`,
              }}
            >
              {/* 영역 크기 표시 */}
              <div className="absolute -top-7 left-0 bg-black/70 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                {Math.round(rect.w * (imgRef.current?.naturalWidth || 0) / (containerRef.current?.clientWidth || 1))} x{' '}
                {Math.round(rect.h * (imgRef.current?.naturalHeight || 0) / (containerRef.current?.clientHeight || 1))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* 확인/취소 버튼 */}
      {selection && (
        <div
          className="absolute z-10 flex gap-2"
          style={{
            left: selection.x + selection.w - 76,
            top: selection.y + selection.h + 8,
          }}
        >
          <button
            onClick={handleConfirm}
            className="flex items-center gap-1 px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 shadow-lg transition-colors"
          >
            <Check size={16} />
            확인
          </button>
          <button
            onClick={onCancel}
            className="flex items-center gap-1 px-3 py-1.5 bg-slate-600 text-white text-sm rounded-lg hover:bg-slate-700 shadow-lg transition-colors"
          >
            <X size={16} />
            취소
          </button>
        </div>
      )}

      {/* 안내 텍스트 */}
      {!selection && !isDrawing && (
        <div className="absolute top-8 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm px-4 py-2 rounded-lg">
          마우스로 드래그하여 캡처할 영역을 선택하세요 (ESC: 취소)
        </div>
      )}
    </div>
  );
};
